import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { UiComponent } from '@df/ui/ui.component';

@Component({
    standalone: true,
    selector: 'page-header-promobar-countdown',
    template: '{{ value }}',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageHeaderPromobarCountdownComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    date?: Date;

    protected value = '';
    private interval?: number;

    override ngOnInit() {
        this.update();
        super.ngOnInit();
    }

    override ngOnChanges() {
        this.update();
    }

    override ngOnDestroy(): void {
        this.removeInterval();
        super.ngOnDestroy();
    }

    private update() {
        const value = this.getValue();
        if (this.value !== value) {
            this.value = value;
            this.detectChanges();
            // make sure interval is set or removed depending on what is needed
            if (!value) {
                this.removeInterval();
            } else {
                if (!this.interval) {
                    this.interval = this.timeoutService.setInterval(() => this.update(), 1000);
                }
            }
        }
    }

    private removeInterval() {
        clearInterval(this.interval);
        this.interval = undefined;
    }

    private getValue() {
        if (!this.date) {
            return '';
        }
        const now = Date.now();
        let diff = this.date.getTime() - now;

        if (diff < 0) {
            return '';
        }

        const TIME_YEAR = 31104000000;
        const TIME_MONTH = 2592000000;
        const TIME_DAY = 86400000;
        const TIME_HOUR = 3600000;

        /**
         * plural is used for plural value, singular for singular values - if you dont want unit to show then leave empty
         * if you want to skip a unit just remove it
         * use function decides when the unit is shown
         */
        const units = [
            // year
            { plural: 'y', singular: 'y', divider: TIME_YEAR, use: time => time > TIME_YEAR },
            // month
            { plural: 'm', singular: 'm', divider: TIME_MONTH, use: time => time < TIME_YEAR && time > TIME_MONTH },
            // week
            { plural: 'w', singular: 'w', divider: 7 * TIME_DAY, use: time => time < TIME_MONTH && time > 7 * TIME_DAY },
            // day
            { plural: 'd', singular: 'd', divider: TIME_DAY, use: time => time < TIME_MONTH && time > TIME_DAY },
            // dayhour
            { plural: 'h', singular: 'h', divider: TIME_HOUR, digits: 2, use: time => time < TIME_DAY },
            // minute
            { plural: 'm', singular: 'm', divider: 60000, digits: 2, use: time => time < TIME_DAY },
            // sekond
            { plural: 's', singular: 's', divider: 1000, digits: 2, use: time => time < TIME_DAY }
        ];

        return units
            .filter(unit => !unit.use || unit.use(diff))
            .map(unit => {
                let value: number;

                if (diff > 0) {
                    value = Math.floor(diff / unit.divider);
                    diff -= value * unit.divider;
                } else {
                    value = 0;
                }

                return `${unit.digits ? (value + '').padStart(unit.digits, '0') : value}${value === 1 ? unit.singular : unit.plural}`;
            })
            .join(' : ');
    }
}
